import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { kes } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Warehouse, Truck, ClipboardList, History, ClipboardCheck, CalendarRange, FileUp, Flame } from "lucide-react";
import { isAccountant as canAccount, isManager as canManage } from "@/lib/roles";
import SectionNav, { SectionLayout } from "@/components/SectionNav";

// Store (warehouse) inventory for lubes and accessories. LPG never goes through the store — the gas
// supplier delivers straight to the forecourt — so it has its own delivery tracker and recon here. Supplier deliveries arrive in the
// store; each shift's "added from store" moves stock onto the forecourt.
// Roles: the manager records deliveries and submits blind stock counts (no book balances shown);
// the accountant owns the reconciliation — balances, count variances, posting counts, history.

const todayISO = () => new Date().toISOString().slice(0, 10);
const qty = (v) => (Math.round(Number(v || 0) * 1000) / 1000).toLocaleString("en-KE");
const signed = (v) => `${v > 0 ? "+" : ""}${qty(v)}`;
const TYPE_LABEL = { delivery: "Delivery in", transfer_in: "Transfer in", transfer_out: "Transfer out", store_sale: "Sold on a shift", to_forecourt: "To forecourt", count: "Stock count", opening: "Opening stock", adjustment: "Adjustment" };
const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

export default function Store() {
  const { user } = useAuth();
  const isAccountant = canAccount(user);
  const canEnter = isAccountant || canManage(user);
  const [tab, setTab] = useState(isAccountant ? "stock" : "deliveries");
  const [items, setItems] = useState([]);
  const [lpgItems, setLpgItems] = useState([]);
  const [stock, setStock] = useState(null);
  const [counts, setCounts] = useState([]);
  const [historyItem, setHistoryItem] = useState(null);

  const load = useCallback(async () => {
    try {
      const reqs = [api.get("/store/items"), api.get("/store/counts"), api.get("/store/lpg/items")];
      if (isAccountant) reqs.push(api.get("/store/stock"));
      const [it, ct, lpg, st] = await Promise.all(reqs);
      setItems(it.data);
      setLpgItems(lpg.data);
      setCounts(ct.data);
      if (st) setStock(st.data);
    } catch (e) {
      err(e);
    }
  }, [isAccountant]);
  useEffect(() => { load(); }, [load]);

  const openHistory = (itemId) => { setHistoryItem(itemId); setTab("history"); };
  const pending = counts.filter((c) => c.status === "pending").length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900">Store</h1>
        <p className="text-sm text-muted-foreground">
          Lubes, LPG &amp; accessories in the store (warehouse). Supplier deliveries come in here; each shift&apos;s
          &ldquo;Added from store&rdquo; moves stock out to the forecourt.
          {!isAccountant && " Stock counts you submit go to the accountant for reconciliation."}
        </p>
      </div>
      <StoreDownloads isAccountant={isAccountant} />

      {isAccountant && stock && !stock.counted && (
        <Card className="border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          The store has no posted stock count yet. The first count you post (from <b>Counts to review</b>) becomes the opening stock.
        </Card>
      )}
      <Tabs value={tab} onValueChange={setTab}>
        <SectionLayout nav={<SectionNav label="Store" value={tab} onChange={setTab} groups={[
          { label: "Lubes store", items: [
            { value: "deliveries", label: "Deliveries" },
            { value: "transfers", label: "Transfers" },
            canEnter && { value: "count", label: "Stock count" },
            isAccountant && { value: "review", label: "Counts to review", badge: pending || null },
            isAccountant && { value: "stock", label: "Reconciliation" },
            isAccountant && { value: "monthly", label: "Monthly recon" },
            isAccountant && { value: "history", label: "Item history" },
          ] },
          { label: "LPG", items: [{ value: "lpg", label: "Deliveries" }, isAccountant && { value: "lpg-recon", label: "LPG recon" }] },
        ]} />}>
        <TabsContent value="lpg" className="mt-0"><DeliveriesTab lpg items={lpgItems} canEnter={canEnter} canDelete={isAccountant} reload={load} /></TabsContent>
        {isAccountant && <TabsContent value="lpg-recon" className="mt-0"><LpgReconTab /></TabsContent>}
        {isAccountant && <TabsContent value="monthly" className="mt-0"><MonthlyTab /></TabsContent>}
        {isAccountant && <TabsContent value="stock" className="mt-0"><StockTab stock={stock} onHistory={openHistory} /></TabsContent>}
        {isAccountant && <TabsContent value="review" className="mt-0"><ReviewTab counts={counts} reload={load} /></TabsContent>}
        <TabsContent value="deliveries" className="mt-0"><DeliveriesTab items={items} canEnter={canEnter} canDelete={isAccountant} reload={load} /></TabsContent>
        <TabsContent value="transfers" className="mt-0"><TransfersTab items={items} canEnter={canEnter} canDelete={isAccountant} reload={load} /></TabsContent>
        {canEnter && <TabsContent value="count" className="mt-0"><CountTab items={items} counts={counts} reload={load} /></TabsContent>}
        {isAccountant && <TabsContent value="history" className="mt-0"><HistoryTab items={items} itemId={historyItem} setItemId={setHistoryItem} /></TabsContent>}
        </SectionLayout>
      </Tabs>
    </div>
  );
}

function StockTab({ stock, onHistory }) {
  if (!stock) return <div className="py-10 text-center"><Loader2 className="inline h-5 w-5 animate-spin" /></div>;
  const totalValue = stock.items.reduce((a, i) => a + (i.store_value_at_cost || 0), 0);
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between border-b bg-slate-50 px-4 py-3 text-sm">
        <span className="font-semibold text-slate-800">Book stock as at {stock.as_of}</span>
        <span className="text-muted-foreground">Store value at cost: <b className="font-mono-fin text-slate-800">KSh {kes(totalValue)}</b></span>
      </div>
      <div className="overflow-x-auto">
        <table className="recon-table">
          <thead><tr>
            <th className="text-left">Item</th><th>In store</th><th>On forecourt</th><th>Total</th>
            <th title="Deliveries in, plus transfers in from / out to other sites">Delivered &amp; transferred (all time)</th><th>Moved to forecourt</th><th>Cost price</th><th>Store value</th><th />
          </tr></thead>
          <tbody>
            {stock.items.map((i) => (
              <tr key={i.item_id} className={i.negative ? "bg-rose-50" : ""} data-testid={`store-row-${i.item_id}`}>
                <td className="!text-left font-medium">{i.name} <span className="text-xs text-muted-foreground">({i.category})</span></td>
                <td className={`calc-cell font-semibold ${i.negative ? "text-rose-600" : ""}`} title={i.negative ? "More moved to the forecourt than the store held — a delivery may be missing, or post a stock count" : undefined}>{qty(i.store)}</td>
                <td className="calc-cell">{qty(i.forecourt)}</td>
                <td className="calc-cell">{qty(i.total)}</td>
                <td className="calc-cell text-muted-foreground">{qty(i.delivered)}</td>
                <td className="calc-cell text-muted-foreground">{qty(i.to_forecourt)}</td>
                <td className="calc-cell">{kes(i.cost_price)}</td>
                <td className="calc-cell">{kes(i.store_value_at_cost)}</td>
                <td className="text-center"><button className="text-xs text-brand underline-offset-2 hover:underline" onClick={() => onHistory(i.item_id)}>History</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function ReviewTab({ counts, reload }) {
  const [busy, setBusy] = useState(null);
  const act = async (id, path, msg) => {
    setBusy(id);
    try {
      const { data } = path === "delete" ? await api.delete(`/store/counts/${id}`) : await api.post(`/store/counts/${id}/${path}`);
      toast.success(path === "post" ? `Count posted — ${data.adjusted} item(s) adjusted` : msg);
      await reload();
    } catch (e) {
      err(e);
    } finally {
      setBusy(null);
    }
  };
  if (counts.length === 0) return <p className="text-sm text-muted-foreground">No stock counts submitted yet.</p>;
  return (
    <div className="space-y-4">
      {counts.map((c) => (
        <Card key={c.id} className="overflow-hidden" data-testid={`count-card-${c.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-slate-50 px-4 py-3 text-sm">
            <div>
              <span className="font-semibold text-slate-800">Count of {c.date}</span>
              <span className="ml-2 text-xs text-muted-foreground">by {c.created_by}{c.note ? ` · ${c.note}` : ""}</span>
              <span className={`ml-2 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${c.status === "posted" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                {c.status === "posted" ? (c.opening ? "posted · opening stock" : "posted") : "awaiting review"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Store variance at cost: <b className={`font-mono-fin ${c.variance_value < 0 ? "text-rose-600" : "text-slate-800"}`}>KSh {kes(c.variance_value)}</b></span>
              <span className="text-muted-foreground">Forecourt: <b className={`font-mono-fin ${c.forecourt_variance_value < 0 ? "text-rose-600" : "text-slate-800"}`}>KSh {kes(c.forecourt_variance_value)}</b></span>
              {c.status === "pending" ? (
                <Button size="sm" className="bg-brand hover:bg-brand/90" disabled={busy === c.id} onClick={() => act(c.id, "post")} data-testid={`count-post-${c.id}`}>Post count</Button>
              ) : (
                <Button size="sm" variant="outline" disabled={busy === c.id} onClick={() => act(c.id, "unpost", "Count reopened")}>Reopen</Button>
              )}
              <Button size="icon" variant="ghost" title="Delete count" disabled={busy === c.id}
                onClick={() => window.confirm("Delete this count (and any adjustments it posted)?") && act(c.id, "delete", "Count deleted")}>
                <Trash2 className="h-4 w-4 text-rose-500" />
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
          <table className="recon-table">
            <thead>
              <tr><th className="text-left" rowSpan={2}>Item</th><th colSpan={4}>Store (posting adjusts the book)</th><th colSpan={4}>Forecourt (checked against the shifts)</th></tr>
              <tr><th>Book</th><th>Counted</th><th>Variance</th><th>At cost</th><th>Per shifts</th><th>Counted</th><th>Variance</th><th>At cost</th></tr>
            </thead>
            <tbody>
              {c.lines.map((ln) => (
                <tr key={ln.item_id}>
                  <td className="!text-left">{ln.item_name}</td>
                  {ln.counted == null ? <td colSpan={4} className="text-center text-xs text-muted-foreground">not counted</td> : (<>
                    <td className="calc-cell">{qty(ln.book)}</td>
                    <td className="calc-cell font-semibold">{qty(ln.counted)}</td>
                    <td className={`calc-cell ${ln.variance ? (ln.variance < 0 ? "text-rose-600" : "text-emerald-700") : ""}`}>{ln.variance ? signed(ln.variance) : "—"}</td>
                    <td className={`calc-cell ${ln.variance_value < 0 ? "text-rose-600" : ""}`}>{ln.variance ? kes(ln.variance_value) : ""}</td>
                  </>)}
                  {ln.forecourt == null ? <td colSpan={4} className="text-center text-xs text-muted-foreground">not counted</td> : (<>
                    <td className="calc-cell">{qty(ln.forecourt_book)}</td>
                    <td className="calc-cell font-semibold">{qty(ln.forecourt)}</td>
                    <td className={`calc-cell ${ln.forecourt_variance ? (ln.forecourt_variance < 0 ? "text-rose-600" : "text-emerald-700") : ""}`}>{ln.forecourt_variance ? signed(ln.forecourt_variance) : "—"}</td>
                    <td className={`calc-cell ${ln.forecourt_variance_value < 0 ? "text-rose-600" : ""}`}>{ln.forecourt_variance ? kes(ln.forecourt_variance_value) : ""}</td>
                  </>)}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Card>
      ))}
    </div>
  );
}

function DeliveriesTab({ items, canEnter, canDelete, reload, lpg = false }) {
  const base = lpg ? "/store/lpg/deliveries" : "/store/deliveries";
  const blankLine = () => ({ item_id: "", qty: "", unit_cost: "" });
  const blankForm = (supplier = "") => ({ date: todayISO(), supplier, invoice_no: "", note: "", lines: [blankLine()] });
  const [form, setForm] = useState(blankForm());
  const [invoice, setInvoice] = useState(null); // an uploaded supplier invoice the form was filled from
  const [reading, setReading] = useState(false);
  const [list, setList] = useState([]);
  const [saving, setSaving] = useState(false);

  const readInvoice = async (file) => {
    if (!file) return;
    setReading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: inv } = await api.post("/store/deliveries/read-invoice", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setInvoice(inv);
      setForm({
        date: inv.delivery_date || inv.invoice_date || todayISO(),
        supplier: inv.supplier || "",
        invoice_no: inv.invoice_no || "",
        note: [inv.order_no && `Order ${inv.order_no}`, inv.delivery_no && `Delivery ${inv.delivery_no}`].filter(Boolean).join(" · "),
        lines: inv.lines.map((l) => ({
          item_id: l.item_id || "", qty: l.units ?? "", unit_cost: l.unit_cost_incl_vat ?? "",
          material_no: l.material_no, description: l.description, inv: l,
        })),
      });
      if (inv.already_recorded) toast.warning(`Invoice ${inv.invoice_no} was already recorded on ${inv.already_recorded.date} — check before saving again`);
      else toast.success(`Read ${inv.lines.length} line(s) from invoice ${inv.invoice_no} — check them, then save`);
    } catch (e) {
      err(e);
    } finally {
      setReading(false);
    }
  };

  const loadList = useCallback(async () => {
    try {
      const { data } = await api.get(base);
      setList(data);
    } catch (e) {
      err(e);
    }
  }, [base]);
  useEffect(() => { loadList(); }, [loadList]);

  const setLine = (idx, field, value) => setForm((f) => ({ ...f, lines: f.lines.map((l, i) => (i === idx ? { ...l, [field]: value } : l)) }));
  const save = async () => {
    setSaving(true);
    try {
      await api.post(base, {
        date: form.date, supplier: form.supplier, invoice_no: form.invoice_no, note: form.note,
        ...(invoice ? { order_no: invoice.order_no || "", delivery_no: invoice.delivery_no || "", invoice_gross: invoice.gross_total, source: "invoice_pdf" } : {}),
        lines: form.lines.filter((l) => l.item_id && Number(l.qty)).map((l) => ({
          item_id: l.item_id, qty: Number(l.qty), unit_cost: Number(l.unit_cost || 0),
          material_no: l.material_no || "", description: l.description || "",
        })),
      });
      toast.success("Delivery recorded in the store");
      setInvoice(null);
      setForm(blankForm(form.supplier));
      await Promise.all([loadList(), reload()]);
    } catch (e) {
      err(e);
    } finally {
      setSaving(false);
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this delivery?")) return;
    try {
      await api.delete(`${base}/${id}`);
      toast.success("Deleted");
      await Promise.all([loadList(), reload()]);
    } catch (e) {
      err(e);
    }
  };
  const lineTotal = form.lines.reduce((a, l) => a + Number(l.qty || 0) * Number(l.unit_cost || 0), 0);

  return (
    <div className="space-y-4">
      {canEnter && (
        <Card className="p-5">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-display font-semibold text-slate-800">{lpg ? "Record an LPG delivery (straight to the forecourt)" : "Record a delivery into the store"}</h3>
            {!lpg && <div className="flex items-center gap-2">
              {invoice && <Button variant="ghost" size="sm" onClick={() => { setInvoice(null); setForm(blankForm()); }}>Clear</Button>}
              <label className={`inline-flex cursor-pointer items-center rounded-md border px-3 py-1.5 text-sm font-medium hover:bg-slate-50 ${reading ? "pointer-events-none opacity-60" : ""}`} data-testid="dlv-upload">
                {reading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileUp className="mr-1.5 h-4 w-4" />}
                Upload Vivo invoice (PDF)
                <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => { readInvoice(e.target.files?.[0]); e.target.value = ""; }} />
              </label>
            </div>}
          </div>
          {invoice && (
            <div className={`mb-3 rounded-md border p-3 text-xs ${invoice.already_recorded ? "border-rose-300 bg-rose-50 text-rose-900" : "border-sky-200 bg-sky-50 text-sky-900"}`}>
              {invoice.already_recorded
                ? <b>This invoice is already recorded (delivery of {invoice.already_recorded.date}, by {invoice.already_recorded.created_by}). Saving it again will be refused.</b>
                : <>Filled from <b>{invoice.supplier} invoice {invoice.invoice_no}</b>{invoice.delivered_to ? ` · delivered to ${invoice.delivered_to}` : ""} · invoice date {invoice.invoice_date}. Cartons are converted to units using the pack size (e.g. 12X1L = 12 per carton); cost is per unit including VAT. <b>Check every line before saving.</b></>}
            </div>
          )}
          <div className="grid gap-3 md:grid-cols-4">
            <div className="space-y-1.5"><Label className="text-xs">Date received</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="dlv-date" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Supplier</Label>
              <Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} placeholder={lpg ? "e.g. Afrigas" : "e.g. Vivo Energy"} data-testid="dlv-supplier" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Invoice / delivery note no.</Label>
              <Input value={form.invoice_no} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} data-testid="dlv-invoice" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Note</Label>
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="dlv-note" /></div>
          </div>
          <table className="recon-table mt-4">
            <thead><tr><th className="text-left">Item</th><th>Quantity (units)</th><th>Cost price per unit (KES)</th><th>Line total</th><th className="w-10" /></tr></thead>
            <tbody>
              {form.lines.map((l, idx) => (
                <tr key={idx}>
                  <td className="!text-left">
                    <select className={`grid-input !text-left ${l.inv && !l.item_id ? "!border-rose-400 !bg-rose-50" : ""}`} style={{ textAlign: "left" }} value={l.item_id} onChange={(e) => setLine(idx, "item_id", e.target.value)} data-testid={`dlv-item-${idx}`}>
                      <option value="">— select item —</option>
                      {items.map((i) => <option key={i.item_id} value={i.item_id}>{i.name}</option>)}
                    </select>
                    {l.inv && (
                      <div className="mt-1 text-[11px] leading-snug text-muted-foreground">
                        Invoice: <span className="font-mono">{l.inv.material_no}</span> {l.inv.description} — {qty(l.inv.qty)} {l.inv.uom}
                        {l.inv.pack_size ? ` × ${l.inv.pack_size} = ${qty(l.inv.units)} units` : ""} · KSh {kes(l.inv.gross)} incl. VAT
                        <div className={!l.item_id ? "font-semibold text-rose-600" : l.inv.match === "code" && l.item_id === l.inv.item_id ? "text-emerald-700" : "text-amber-700"}>
                          {!l.item_id ? "Not matched — choose the item (it will be remembered for next time)"
                            : l.item_id !== l.inv.item_id ? "Item changed by you — will be remembered for next time"
                            : l.inv.match === "code" ? "Matched by Vivo code" : "Suggested by name — check it is the right item"}
                          {!l.inv.pack_size && <span className="ml-1 font-semibold text-rose-600">· pack size not on invoice — enter units</span>}
                        </div>
                      </div>
                    )}
                  </td>
                  <td><input type="number" className="grid-input" value={l.qty} onChange={(e) => setLine(idx, "qty", e.target.value)} data-testid={`dlv-qty-${idx}`} /></td>
                  <td><input type="number" className="grid-input" value={l.unit_cost} onChange={(e) => setLine(idx, "unit_cost", e.target.value)} data-testid={`dlv-cost-${idx}`} /></td>
                  <td className="calc-cell">{kes(Number(l.qty || 0) * Number(l.unit_cost || 0))}</td>
                  <td className="text-center"><button className="text-rose-500" onClick={() => setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }))}>×</button></td>
                </tr>
              ))}
              <tr className="font-semibold"><td className="!text-left" colSpan={3}>Delivery total</td><td className="calc-cell">{kes(lineTotal)}</td><td /></tr>
              {invoice && (
                <tr className={Math.abs(lineTotal - invoice.gross_total) > 1 ? "text-rose-600" : "text-emerald-700"}>
                  <td className="!text-left text-xs" colSpan={3}>Invoice gross amount (net {kes(invoice.net_total)} + VAT {kes(invoice.vat_total)})</td>
                  <td className="calc-cell text-xs">{kes(invoice.gross_total)}{Math.abs(lineTotal - invoice.gross_total) > 1 ? ` (diff ${kes(lineTotal - invoice.gross_total)})` : " ✓"}</td><td />
                </tr>
              )}
            </tbody>
          </table>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => setForm((f) => ({ ...f, lines: [...f.lines, blankLine()] }))} data-testid="dlv-add-line"><Plus className="mr-1 h-4 w-4" />Add item</Button>
            <Button onClick={save} disabled={saving || !form.lines.some((l) => l.item_id && Number(l.qty)) || form.lines.some((l) => l.inv && (!l.item_id || !Number(l.qty)))} className="bg-brand hover:bg-brand/90" data-testid="dlv-save">
              {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Save delivery
            </Button>
            <span className="text-xs text-muted-foreground">
              A cost price entered here becomes that item&apos;s cost price (used for gross profit).
              {lpg && " Record full cylinders received from the supplier; they should match the \u201cAddition\u201d on that day's shift."}
              {!canDelete && " Only the accountant can delete a delivery — ask them if one was entered wrongly."}
            </span>
          </div>
        </Card>
      )}
      <Card className="overflow-hidden">
        <div className="border-b bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800">{lpg ? "LPG deliveries received" : "Deliveries received"}</div>
        <div className="overflow-x-auto">
          <table className="recon-table">
            <thead><tr><th className="text-left">Date</th><th className="text-left">Supplier</th><th className="text-left">Invoice</th><th className="text-left">Items</th><th>Total cost</th><th className="text-left">By</th><th /></tr></thead>
            <tbody>
              {list.length === 0 && <tr><td colSpan={7} className="py-4 text-center text-sm text-muted-foreground">No deliveries recorded yet.</td></tr>}
              {list.map((d) => (
                <tr key={d.id}>
                  <td className="!text-left">{d.date}</td>
                  <td className="!text-left">{d.supplier || "—"}</td>
                  <td className="!text-left">{d.invoice_no || "—"}</td>
                  <td className="!text-left text-xs">{d.lines.map((l) => `${l.item_name} × ${qty(l.qty)}`).join(" · ")}</td>
                  <td className="calc-cell">{kes(d.total_cost)}</td>
                  <td className="!text-left text-xs text-muted-foreground">{d.created_by}</td>
                  <td className="text-center">{canDelete && <Button variant="ghost" size="icon" onClick={() => remove(d.id)} title="Delete"><Trash2 className="h-4 w-4 text-rose-500" /></Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// Stock moved between this store and another site's (e.g. oils sent from one branch to another).
function TransfersTab({ items, canEnter, canDelete, reload }) {
  const blankLine = () => ({ item_id: "", qty: "", unit_cost: "" });
  const blank = (direction = "in", site = "Thome") => ({ date: todayISO(), direction, site, note: "", lines: [blankLine()] });
  const [form, setForm] = useState(blank());
  const [list, setList] = useState([]);
  const [saving, setSaving] = useState(false);
  const loadList = useCallback(async () => {
    try { setList((await api.get("/store/transfers")).data); } catch (e) { err(e); }
  }, []);
  useEffect(() => { loadList(); }, [loadList]);
  const setLine = (idx, field, value) => setForm((f) => ({ ...f, lines: f.lines.map((l, i) => (i === idx ? { ...l, [field]: value } : l)) }));
  const out = form.direction === "out";
  const save = async () => {
    setSaving(true);
    try {
      await api.post("/store/transfers", {
        date: form.date, direction: form.direction, site: form.site, note: form.note,
        lines: form.lines.filter((l) => l.item_id && Number(l.qty)).map((l) => ({ item_id: l.item_id, qty: Number(l.qty), unit_cost: Number(l.unit_cost || 0) })),
      });
      toast.success(out ? `Transfer out to ${form.site} recorded` : `Transfer in from ${form.site} recorded`);
      setForm(blank(form.direction, form.site));
      await Promise.all([loadList(), reload()]);
    } catch (e) {
      err(e);
    } finally {
      setSaving(false);
    }
  };
  // accountant only: an Excel of the transfers in a period, and the same sent to the accounts / managers to verify
  const [range, setRange] = useState({ from: todayISO().slice(0, 8) + "01", to: todayISO() });
  const [extra, setExtra] = useState("");
  const exportTransfers = async () => {
    try {
      const res = await api.get("/store/transfers/export", { params: { date_from: range.from, date_to: range.to }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `stock_transfers_${range.from}_${range.to}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { err(e); }
  };
  const sendTransfers = async (id) => {
    try {
      const { data } = await api.post("/store/transfers/send", { id: id || "", date_from: range.from, date_to: range.to, emails: extra.split(/[,;\s]+/).filter(Boolean) });
      toast.success(`Sent ${data.count} transfer${data.count === 1 ? "" : "s"} to ${data.sent_to.join(", ")}`);
    } catch (e) { err(e); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this transfer?")) return;
    try {
      await api.delete(`/store/transfers/${id}`);
      toast.success("Deleted");
      await Promise.all([loadList(), reload()]);
    } catch (e) {
      err(e);
    }
  };
  return (
    <div className="space-y-4">
      {canEnter && (
        <Card className="p-5">
          <h3 className="mb-1 font-display font-semibold text-slate-800">Record a stock transfer</h3>
          <p className="mb-3 text-xs text-muted-foreground">
            Stock moved between this store and a sister site&apos;s (Thome, Kariokor, Haile Selassie) or another site. The sending site records it as OUT
            and the receiving site as IN, so each store shows it. The books move the stock between the branches at cost when the sender records it; a transfer to or from any other site is owed to or by that site.
          </p>
          <div className="grid gap-3 md:grid-cols-4">
            <div className="space-y-1.5"><Label className="text-xs">Direction</Label>
              <select className="grid-input !text-left" style={{ textAlign: "left" }} value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })} data-testid="trf-direction">
                <option value="in">In - from another site</option>
                <option value="out">Out - to another site</option>
              </select></div>
            <div className="space-y-1.5"><Label className="text-xs">{out ? "To site" : "From site"}</Label>
              <Input list="trf-sites" value={form.site} onChange={(e) => setForm({ ...form, site: e.target.value })} data-testid="trf-site" />
              <datalist id="trf-sites">{['Thome', 'Haile Selassie'].map((s) => <option key={s} value={s} />)}</datalist></div>
            <div className="space-y-1.5"><Label className="text-xs">Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="trf-date" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Note</Label>
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. returned, to be paid" data-testid="trf-note" /></div>
          </div>
          <table className="recon-table mt-4">
            <thead><tr><th className="text-left">Item</th><th>Quantity (units)</th><th>Cost per unit (KES)</th><th>Line total</th><th className="w-10" /></tr></thead>
            <tbody>
              {form.lines.map((l, idx) => (
                <tr key={idx}>
                  <td className="!text-left">
                    <select className="grid-input !text-left" style={{ textAlign: "left" }} value={l.item_id} onChange={(e) => setLine(idx, "item_id", e.target.value)} data-testid={`trf-item-${idx}`}>
                      <option value="">— select item —</option>
                      {items.map((i) => <option key={i.item_id} value={i.item_id}>{i.name}</option>)}
                    </select>
                  </td>
                  <td><input type="number" className="grid-input" value={l.qty} onChange={(e) => setLine(idx, "qty", e.target.value)} data-testid={`trf-qty-${idx}`} /></td>
                  <td><input type="number" className="grid-input" value={l.unit_cost} placeholder={out ? "item's cost" : ""} onChange={(e) => setLine(idx, "unit_cost", e.target.value)} data-testid={`trf-cost-${idx}`} /></td>
                  <td className="calc-cell">{l.unit_cost ? kes(Number(l.qty || 0) * Number(l.unit_cost || 0)) : ""}</td>
                  <td className="text-center"><button className="text-rose-500" onClick={() => setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }))}>×</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => setForm((f) => ({ ...f, lines: [...f.lines, blankLine()] }))}><Plus className="mr-1 h-4 w-4" />Add item</Button>
            <Button onClick={save} disabled={saving || !form.site.trim() || !form.lines.some((l) => l.item_id && Number(l.qty)) || (!out && form.lines.some((l) => l.item_id && Number(l.qty) && !Number(l.unit_cost)))} className="bg-brand hover:bg-brand/90" data-testid="trf-save">
              {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Save transfer
            </Button>
            <span className="text-xs text-muted-foreground">
              {out ? "Leave the cost empty to use the item's cost price. The store must hold the quantity." : "Enter the cost per unit incl. VAT, as the other site charges it."}
              {!canDelete && " Only the accountant can delete a transfer."}
            </span>
          </div>
        </Card>
      )}
      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 border-b bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800">
          <span className="mr-auto">Transfers</span>
          {canDelete && (
            <>
              <Input type="date" className="h-8 w-36" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} data-testid="trf-from" />
              <Input type="date" className="h-8 w-36" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} data-testid="trf-to" />
              <Input className="h-8 w-56" value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="also send to (emails, optional)" data-testid="trf-extra" />
              <Button variant="outline" size="sm" onClick={exportTransfers} data-testid="trf-export">Export Excel</Button>
              <Button variant="outline" size="sm" onClick={() => sendTransfers("")} data-testid="trf-send-all">Email these to accounts / managers</Button>
            </>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="recon-table">
            <thead><tr><th className="text-left">Date</th><th className="text-left">In / out</th><th className="text-left">Site</th><th className="text-left">Items</th><th>Total cost</th><th className="text-left">Note</th><th className="text-left">By</th><th /></tr></thead>
            <tbody>
              {list.length === 0 && <tr><td colSpan={8} className="py-4 text-center text-sm text-muted-foreground">No transfers recorded yet.</td></tr>}
              {list.map((t) => (
                <tr key={t.id}>
                  <td className="!text-left">{t.date}</td>
                  <td className="!text-left">{t.direction === "in" ? "In" : "Out"}</td>
                  <td className="!text-left">{t.site}</td>
                  <td className="!text-left text-xs">{t.lines.map((l) => `${l.item_name} × ${qty(l.qty)}`).join(" · ")}</td>
                  <td className="calc-cell">{kes(t.total_cost)}</td>
                  <td className="!text-left text-xs">{t.note}</td>
                  <td className="!text-left text-xs text-muted-foreground">{t.created_by}</td>
                  <td className="whitespace-nowrap text-center">{canDelete && <Button variant="ghost" size="sm" onClick={() => sendTransfers(t.id)} title="Email this transfer to the accounts / managers" data-testid={`trf-send-${t.id}`}>Email</Button>}{canDelete && <Button variant="ghost" size="icon" onClick={() => remove(t.id)} title="Delete"><Trash2 className="h-4 w-4 text-rose-500" /></Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// Blind count: the person counting never sees the book balance or the difference.
function CountTab({ items, counts, reload }) {
  const [date, setDate] = useState(todayISO());
  const [note, setNote] = useState("");
  const [entered, setEntered] = useState({});
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const num = (v) => (v === "" || v === undefined ? null : Number(v));
      const lines = Object.entries(entered).map(([item_id, v]) => ({ item_id, counted: num(v.store), forecourt: num(v.forecourt) }))
        .filter((l) => l.counted !== null || l.forecourt !== null);
      await api.post("/store/count", { date, note, lines });
      toast.success("Stock count submitted to the accountant");
      setEntered({});
      setNote("");
      await reload();
    } catch (e) {
      err(e);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-4">
      <Card className="p-5">
        <h3 className="font-display font-semibold text-slate-800">Store stock count</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Count what is physically in the store and, for a month-end stock take, on the forecourt too — enter each separately.
          Leave a box blank if you did not count it. The count goes to the accountant, who reconciles it against the books.
        </p>
        <div className="mt-3 grid gap-3 md:grid-cols-3">
          <div className="space-y-1.5"><Label className="text-xs">Count date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="count-date" /></div>
          <div className="space-y-1.5 md:col-span-2"><Label className="text-xs">Note (optional)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. month-end count" data-testid="count-note" /></div>
        </div>
        <table className="recon-table mt-4">
          <thead><tr><th className="text-left">Item</th><th>In store (units)</th><th>On forecourt (units)</th></tr></thead>
          <tbody>
            {items.map((i) => {
              const v = entered[i.item_id] || {};
              const set = (k, val) => setEntered({ ...entered, [i.item_id]: { ...v, [k]: val } });
              return (
                <tr key={i.item_id}>
                  <td className="!text-left">{i.name} <span className="text-xs text-muted-foreground">({i.category})</span></td>
                  <td><input type="number" min="0" className="grid-input" value={v.store ?? ""} onChange={(e) => set("store", e.target.value)} data-testid={`count-${i.item_id}`} /></td>
                  <td><input type="number" min="0" className="grid-input" value={v.forecourt ?? ""} onChange={(e) => set("forecourt", e.target.value)} data-testid={`count-fc-${i.item_id}`} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Button className="mt-4 bg-brand hover:bg-brand/90" onClick={save} disabled={saving || !Object.values(entered).some((v) => (v.store ?? "") !== "" || (v.forecourt ?? "") !== "")} data-testid="count-save">
          {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Submit stock count
        </Button>
      </Card>
      <Card className="overflow-hidden">
        <div className="border-b bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800">Counts submitted</div>
        <table className="recon-table">
          <thead><tr><th className="text-left">Date</th><th className="text-left">Items counted</th><th className="text-left">By</th><th className="text-left">Status</th></tr></thead>
          <tbody>
            {counts.length === 0 && <tr><td colSpan={4} className="py-4 text-center text-sm text-muted-foreground">No counts submitted yet.</td></tr>}
            {counts.map((c) => (
              <tr key={c.id}>
                <td className="!text-left">{c.date}</td>
                <td className="!text-left text-xs">{c.lines.map((l) => [
                  l.counted != null && `${l.item_name}: ${qty(l.counted)} store`,
                  l.forecourt != null && `${l.counted != null ? "" : `${l.item_name}: `}${qty(l.forecourt)} forecourt`,
                ].filter(Boolean).join(", ")).join(" · ")}</td>
                <td className="!text-left text-xs text-muted-foreground">{c.created_by}</td>
                <td className="!text-left text-xs">{c.status === "posted" ? "Reconciled by accountant" : "Awaiting accountant"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// Month stock recon over store + forecourt: opening + deliveries + LPG returned − sold = expected,
// against the actual closing (store book after posted counts + forecourt per the last shift).
function MonthlyTab() {
  const [month, setMonth] = useState(todayISO().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!month) return;
    setLoading(true);
    api.get("/store/monthly", { params: { month } }).then(({ data }) => setData(data)).catch(err).finally(() => setLoading(false));
  }, [month]);
  const t = data?.totals;
  const vc = (v) => (v ? (v < 0 ? "text-rose-600" : "text-emerald-700") : "");
  return (
    <div className="space-y-4">
      <Card className="flex flex-wrap items-center gap-4 p-4 text-sm">
        <div className="flex items-center gap-2"><Label className="text-xs">Month</Label>
          <Input type="month" className="w-44" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="monthly-month" /></div>
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        {data && <span className="text-muted-foreground">{data.date_from} to {data.date_to} · {data.shifts} shift(s){data.open_shifts ? `, ${data.open_shifts} not yet submitted` : ""}</span>}
        {data && <Button variant="outline" size="sm" className="ml-auto" onClick={() => downloadExcel("/store/monthly/excel", `store_monthly_recon_${month}.xlsx`, { month })} data-testid="monthly-excel">Download Excel</Button>}
      </Card>
      {data && !data.store_counted && (
        <Card className="border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">No store stock count has been posted up to this month, so the store opening is not yet reliable.</Card>
      )}
      {data && (
        <>
          <div className="grid gap-3 md:grid-cols-4">
            {[["Opening stock at cost", t.opening_value], ["Sold at cost", t.sold_value], ["Sales value", t.sold_revenue], ["Variance at cost", t.variance_value]].map(([l, v]) => (
              <Card key={l} className="p-4"><div className="text-xs text-muted-foreground">{l}</div>
                <div className={`font-mono-fin text-lg font-bold ${l.startsWith("Variance") ? vc(v) : "text-slate-800"}`}>KSh {kes(v)}</div></Card>
            ))}
          </div>
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="recon-table">
                <thead>
                  <tr><th className="text-left" rowSpan={2}>Item</th><th colSpan={3}>Opening</th><th rowSpan={2} title="Deliveries in, plus transfers in from / out to other sites">Delivered / transferred</th><th rowSpan={2}>LPG returned</th><th rowSpan={2}>Sold</th><th rowSpan={2}>Expected closing</th><th colSpan={3}>Actual closing</th><th colSpan={3}>Variance</th><th rowSpan={2} title="Latest stock take's forecourt count vs the forecourt per the shifts">Forecourt count check</th></tr>
                  <tr><th>Store</th><th>Forecourt</th><th>Total</th><th>Store</th><th>Forecourt</th><th>Total</th><th>Units</th><th>At cost</th><th>At price</th></tr>
                </thead>
                <tbody>
                  {data.items.map((i) => (
                    <tr key={i.item_id} className={i.variance ? "bg-rose-50/40" : ""}>
                      <td className="!text-left font-medium">{i.name}</td>
                      <td className="calc-cell">{qty(i.opening_store)}</td>
                      <td className="calc-cell">{qty(i.opening_forecourt)}</td>
                      <td className="calc-cell font-semibold">{qty(i.opening)}</td>
                      <td className="calc-cell">{i.delivered ? qty(i.delivered) : ""}</td>
                      <td className="calc-cell">{i.returned ? qty(i.returned) : ""}</td>
                      <td className="calc-cell">{i.sold ? qty(i.sold) : ""}</td>
                      <td className="calc-cell font-semibold">{qty(i.expected)}</td>
                      <td className="calc-cell">{qty(i.closing_store)}</td>
                      <td className="calc-cell">{qty(i.closing_forecourt)}</td>
                      <td className="calc-cell font-semibold">{qty(i.actual)}</td>
                      <td className={`calc-cell font-semibold ${vc(i.variance)}`}>{i.variance ? signed(i.variance) : "—"}</td>
                      <td className={`calc-cell ${vc(i.variance_value)}`}>{i.variance ? kes(i.variance_value) : ""}</td>
                      <td className={`calc-cell ${vc(i.variance_revenue)}`}>{i.variance ? kes(i.variance_revenue) : ""}</td>
                      <td className={`calc-cell ${vc(i.forecourt_count_variance)}`} title={i.forecourt_counted != null ? `Counted ${qty(i.forecourt_counted)} on the forecourt` : undefined}>
                        {i.forecourt_counted == null ? "" : i.forecourt_count_variance ? signed(i.forecourt_count_variance) : "✓"}
                      </td>
                    </tr>
                  ))}
                  <tr className="font-semibold">
                    <td className="!text-left" colSpan={12}>Totals</td>
                    <td className={`calc-cell ${vc(t.variance_value)}`}>{kes(t.variance_value)}</td>
                    <td className={`calc-cell ${vc(t.variance_revenue)}`}>{kes(t.variance_revenue)}</td>
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
          <p className="text-xs text-muted-foreground">
            Sold comes from the shifts&apos; forecourt counts. Variance = actual closing − expected closing: it is the store count adjustments
            posted in the month plus any break between one shift&apos;s closing and the next shift&apos;s opening.
          </p>
        </>
      )}
    </div>
  );
}

// LPG for a month: supplier deliveries vs the shifts' additions (day by day), and the cylinder stock
// recon — opening + added + returned − sold = expected, against the last shift's closing.
function LpgReconTab() {
  const [month, setMonth] = useState(todayISO().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!month) return;
    setLoading(true);
    api.get("/store/lpg/recon", { params: { month } }).then(({ data }) => setData(data)).catch(err).finally(() => setLoading(false));
  }, [month]);
  const vc = (v) => (v ? (v < 0 ? "text-rose-600" : "text-emerald-700") : "");
  const gaps = (data?.days || []).filter((d) => d.gap);
  return (
    <div className="space-y-4">
      <Card className="flex flex-wrap items-center gap-4 p-4 text-sm">
        <div className="flex items-center gap-2"><Label className="text-xs">Month</Label>
          <Input type="month" className="w-44" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="lpg-month" /></div>
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        {data && <span className="text-muted-foreground">{data.date_from} to {data.date_to} · {data.shifts} shift(s)</span>}
        {data && <Button variant="outline" size="sm" className="ml-auto" onClick={() => downloadExcel("/store/lpg/recon/excel", `lpg_recon_${month}.xlsx`, { month })} data-testid="lpg-excel">Download Excel</Button>}
      </Card>
      {data && (
        <>
          <div className="grid gap-3 md:grid-cols-4">
            {[["Deliveries at cost", data.totals.delivered_cost], ["Sales value", data.totals.sold_revenue], ["Sold at cost", data.totals.sold_value], ["Stock variance at cost", data.totals.variance_value]].map(([l, v]) => (
              <Card key={l} className="p-4"><div className="text-xs text-muted-foreground">{l}</div>
                <div className={`font-mono-fin text-lg font-bold ${l.startsWith("Stock") ? vc(v) : "text-slate-800"}`}>KSh {kes(v)}</div></Card>
            ))}
          </div>
          <Card className="overflow-hidden">
            <div className="border-b bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800">Cylinder stock</div>
            <div className="overflow-x-auto">
              <table className="recon-table">
                <thead><tr>
                  <th className="text-left">Item</th><th>Opening</th><th title="Recorded supplier deliveries">Delivered</th><th title="Shifts' Addition column">Added on shifts</th>
                  <th>Delivery gap</th><th>Returned</th><th>Sold</th><th>Expected</th><th>Closing</th><th>Variance</th><th>Sales value</th>
                </tr></thead>
                <tbody>
                  {data.items.map((i) => (
                    <tr key={i.item_id}>
                      <td className="!text-left font-medium">{i.name}</td>
                      <td className="calc-cell">{qty(i.opening)}</td>
                      <td className="calc-cell">{i.delivered ? qty(i.delivered) : ""}</td>
                      <td className="calc-cell">{i.added ? qty(i.added) : ""}</td>
                      <td className={`calc-cell font-semibold ${vc(i.delivery_gap)}`}>{i.delivery_gap ? signed(i.delivery_gap) : "—"}</td>
                      <td className="calc-cell">{i.returned ? qty(i.returned) : ""}</td>
                      <td className="calc-cell">{i.sold ? qty(i.sold) : ""}</td>
                      <td className="calc-cell">{qty(i.expected)}</td>
                      <td className="calc-cell font-semibold">{qty(i.closing)}</td>
                      <td className={`calc-cell font-semibold ${vc(i.variance)}`}>{i.variance ? signed(i.variance) : "—"}</td>
                      <td className="calc-cell">{kes(i.sold_revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
          <Card className="overflow-hidden">
            <div className="border-b bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800">
              Deliveries vs shift additions by day {gaps.length > 0 && <span className="ml-2 rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-rose-700">{gaps.length} not matching</span>}
            </div>
            <table className="recon-table">
              <thead><tr><th className="text-left">Date</th><th className="text-left">Item</th><th>Delivered</th><th>Added on shift</th><th className="text-left">Shift</th><th>Gap</th></tr></thead>
              <tbody>
                {data.days.length === 0 && <tr><td colSpan={6} className="py-4 text-center text-sm text-muted-foreground">No LPG deliveries or additions this month.</td></tr>}
                {data.days.map((d, idx) => (
                  <tr key={idx} className={d.gap ? "bg-rose-50/60" : ""}>
                    <td className="!text-left">{d.date}</td>
                    <td className="!text-left">{d.name}</td>
                    <td className="calc-cell">{d.delivered ? qty(d.delivered) : "—"}</td>
                    <td className="calc-cell">{d.added ? qty(d.added) : "—"}</td>
                    <td className="!text-left text-xs">{d.shifts.join(", ")}</td>
                    <td className={`calc-cell font-semibold ${vc(d.gap)}`}>{d.gap ? signed(d.gap) : "✓"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          <p className="text-xs text-muted-foreground">
            Delivery gap = cylinders recorded as delivered − cylinders added on the shifts. A positive gap is a delivery not put on a shift;
            a negative gap is an addition on a shift with no delivery recorded. Stock variance = a shift&apos;s closing not carried into the next shift&apos;s opening.
          </p>
        </>
      )}
    </div>
  );
}

function HistoryTab({ items, itemId, setItemId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!itemId) { setData(null); return; }
    api.get(`/store/movements/${itemId}`).then(({ data }) => setData(data)).catch(err);
  }, [itemId]);
  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 border-b bg-slate-50 px-4 py-3">
        <span className="text-sm font-semibold text-slate-800">Item</span>
        <select className="grid-input !w-auto !text-left" style={{ textAlign: "left" }} value={itemId || ""} onChange={(e) => setItemId(e.target.value || null)} data-testid="history-item">
          <option value="">— select item —</option>
          {items.map((i) => <option key={i.item_id} value={i.item_id}>{i.name}</option>)}
        </select>
        {data && <span className="text-sm text-muted-foreground">In store now: <b className="text-slate-800">{qty(data.store)}</b></span>}
      </div>
      {data && (
        <table className="recon-table">
          <thead><tr><th className="text-left">Date</th><th className="text-left">Movement</th><th className="text-left">Details</th><th>In</th><th>Out</th><th>Store balance</th></tr></thead>
          <tbody>
            {data.movements.length === 0 && <tr><td colSpan={6} className="py-4 text-center text-sm text-muted-foreground">No movements yet.</td></tr>}
            {data.movements.map((m, i) => (
              <tr key={i}>
                <td className="!text-left">{m.date}</td>
                <td className="!text-left">{TYPE_LABEL[m.type] || m.type}</td>
                <td className="!text-left text-xs">{m.detail}</td>
                <td className="calc-cell text-emerald-700">{m.qty > 0 ? qty(m.qty) : ""}</td>
                <td className="calc-cell text-rose-600">{m.qty < 0 ? qty(-m.qty) : ""}</td>
                <td className="calc-cell font-semibold">{qty(m.balance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}


async function downloadExcel(path, name, params) {
  try {
    const res = await api.get(path, { params, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Download failed");
  }
}

// Excel downloads: the item list for everyone, the stock list (with balances) for the accountant
function StoreDownloads({ isAccountant }) {
  const get = (path, name) => downloadExcel(path, name);
  const btn = "rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50";
  return (
    <div className="flex flex-wrap gap-2" data-testid="store-downloads">
      {isAccountant && <button type="button" className={btn} onClick={() => get("/store/stock/excel", "store_stock.xlsx")}>Download stock list (Excel)</button>}
      <button type="button" className={btn} onClick={() => get("/store/items/excel", "store_items.xlsx")}>Download items (Excel)</button>
    </div>
  );
}
